import type { KeyboardEventHandler } from 'react';
import { useRef, useState } from 'react';

import { Button, Input } from '@poseidon/components/Input2';

import type { SequenceSwitchAttribute, SequenceSwitchBranch } from '~/components/Sequence/sequenceSteps';
import { parseSwitchBound, switchBranchLabel, switchRangeEditParts } from '~/components/Sequence/sequenceSteps';

interface Props {
  attribute: SequenceSwitchAttribute;
  /** The range branch being edited (never the catch-all — "Doesn't match any range" isn't a range). */
  branch: SequenceSwitchBranch;
  /** Persist the new bounds. Only fired when the parsed range actually differs from the current one. */
  onCommit: (next: { minValue: number | null; maxValue: number | null }) => void;
  /** Start in edit mode on mount — a just-added switch focuses its first branch's range straight away
   *  (the range is what matters, not the step). One-shot: it only seeds the initial editing state. */
  autoEdit?: boolean;
  /** Locked view (the per-contact contact-detail graph): render the range as a static, read-only chip. */
  readOnly?: boolean;
}

/**
 * A switch branch's range as a click-to-edit chip. Displays the compact label ("$0–$100K") and, on click,
 * swaps to two number fields — a min and a max, either left blank for "unbounded" — with a dash between.
 * Commits on blur out of the pair / Enter, reverts on Escape. Tolerant of `$`, commas and k/m/b suffixes.
 */
export function SwitchRangeChip({ attribute, branch, onCommit, autoEdit, readOnly = false }: Props) {
  const [editing, setEditing] = useState(autoEdit === true && !readOnly);
  const initial = switchRangeEditParts(branch);
  const [minText, setMinText] = useState(autoEdit === true ? initial.min : '');
  const [maxText, setMaxText] = useState(autoEdit === true ? initial.max : '');
  // Set on Escape so the blur that follows cancels instead of committing.
  const cancelledRef = useRef(false);

  const startEdit = () => {
    const parts = switchRangeEditParts(branch);
    setMinText(parts.min);
    setMaxText(parts.max);
    cancelledRef.current = false;
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const minValue = parseSwitchBound(minText);
    const maxValue = parseSwitchBound(maxText);
    // A non-blank side that fails to parse → keep the prior range, so a typo never silently wipes a bound.
    if ((minText.trim() !== '' && minValue == null) || (maxText.trim() !== '' && maxValue == null)) return;
    if (minValue !== branch.minValue || maxValue !== branch.maxValue) onCommit({ minValue, maxValue });
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    else if (e.key === 'Escape') {
      cancelledRef.current = true;
      e.currentTarget.blur();
    }
  };

  // Locked view: a static range chip — no click-to-edit (a per-contact override can't change routing).
  if (readOnly) {
    return (
      <Button size="2xs" color="yellow" tone="soft" readOnly className="rounded-full font-semibold">
        {switchBranchLabel(attribute, branch)}
      </Button>
    );
  }

  if (editing) {
    // The two fields need more room than a fan column is wide, so the editor floats above the chip as a
    // small popover (solid bg + shadow) — an invisible placeholder chip holds the column's footprint so
    // the surrounding branches don't shift while you edit.
    return (
      <div className="relative">
        <Button size="2xs" readOnly className="invisible rounded-full font-semibold">
          {switchBranchLabel(attribute, branch)}
        </Button>
        <div
          className="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-lg border border-lynch-200 bg-white p-1.5 shadow-md dark:border-slate-700 dark:bg-slate-900"
          // Blur commits only when focus leaves the pair — tabbing between min and max must not commit.
          onBlur={(e) => {
            if (e.currentTarget.contains(e.relatedTarget)) return;
            commit();
          }}>
          <Input
            autoFocus
            size="xs"
            value={minText}
            placeholder="min"
            inputMode="decimal"
            onFocus={(e) => e.target.select()}
            onChange={(e) => setMinText(e.target.value)}
            onKeyDown={onKeyDown}
            className="w-20 text-center"
          />
          <span className="shrink-0 text-slate-400 dark:text-slate-500">–</span>
          <Input
            size="xs"
            value={maxText}
            placeholder="max"
            inputMode="decimal"
            onFocus={(e) => e.target.select()}
            onChange={(e) => setMaxText(e.target.value)}
            onKeyDown={onKeyDown}
            className="w-20 text-center"
          />
        </div>
      </div>
    );
  }

  return (
    <Button size="2xs" color="yellow" tone="soft" tooltip="Edit range" onClick={startEdit} className="rounded-full font-semibold">
      {switchBranchLabel(attribute, branch)}
    </Button>
  );
}
